import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FlaskConical, FileText, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Seo } from "../components/Seo";

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-[#EFEBE6] last:border-0">
      <span className="text-[10px] tracking-[0.12em] uppercase text-[#6E675F]">{label}</span>
      <span className="text-xs font-semibold text-[#1A1A1A] text-right">{value}</span>
    </div>
  );
}

export default function LabResults() {
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/lab-results").then(({ data }) => setResults(data)).catch(() => setResults([]));
  }, []);

  return (
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Lab Results" description="Third-party lab results and certificates of analysis for Eternal Biolabs research peptides." path="/lab-results" />
      <div className="max-w-2xl mb-12">
        <p className="text-xs tracking-[0.3em] uppercase font-semibold text-[#8C7B6B] mb-3">Verified Quality</p>
        <h1 className="font-display text-xl sm:text-3xl uppercase tracking-wide text-[#1A1A1A]">Lab Results</h1>
        <p className="text-base text-[#6E675F] mt-4 leading-relaxed">
          Every batch is independently tested for identity and purity. Browse the latest third-party lab reports
          for our catalog below — each report is linked to its product.
        </p>
      </div>

      {results === null ? (
        <div className="flex justify-center py-24" data-testid="lab-results-loading">
          <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-[#DDD7CF] rounded-lg" data-testid="lab-results-empty">
          <FlaskConical className="w-10 h-10 text-[#8C7B6B] mx-auto mb-4" />
          <p className="text-[#6E675F]">Lab reports are being uploaded — check back soon or contact us for a specific batch.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="lab-results-grid">
          {results.map((r) => (
            <div key={r.id} className="border border-[#DDD7CF] rounded-lg bg-white p-6 flex flex-col" data-testid={`lab-result-card-${r.id}`}>
              <div className="flex items-center gap-4 mb-4">
                <img src={r.image_url} alt={r.name} className="w-16 h-16 rounded-lg object-cover border border-[#EFEBE6]" />
                <div className="min-w-0">
                  <p className="font-medium text-[#1A1A1A] leading-snug">{r.name}</p>
                  <p className="text-sm text-[#6E675F]">{r.dosage}</p>
                </div>
              </div>
              <div className="mb-5">
                <DetailRow label="Purity (HPLC)" value={r.purity} />
                <DetailRow label="Lot Number" value={r.lot_number} />
                <DetailRow label="Test Date" value={r.coa_test_date} />
                <DetailRow label="Tested By" value={r.coa_lab} />
              </div>
              <div className="mt-auto flex flex-col gap-2.5">
                <a href={r.lab_result_url} target="_blank" rel="noopener noreferrer" data-testid={`view-lab-report-${r.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#8C7B6B] text-white text-xs tracking-[0.15em] uppercase font-semibold px-6 py-3 rounded-none hover:bg-[#75665A] transition-colors">
                  <FileText className="w-4 h-4" /> View Lab Report
                </a>
                <Link to={`/product/${r.id}`} data-testid={`lab-result-product-link-${r.id}`}
                  className="inline-flex items-center justify-center gap-1.5 text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] hover:text-[#75665A] py-1.5">
                  View Product <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-[#6E675F] mt-16 border-t border-[#DDD7CF] pt-6">
        For laboratory research use only. Not for human or veterinary consumption.
      </p>
    </div>
  );
}
